import {
  ENDPOINT_FARMER_ACTION_INSPECT,
  ENDPOINT_RESPONSIBLE_USERS_BY_DISTRICT,
  ENDPOINT_RESPONSIBLE_USERS_COUNT,
  ENDPOINT_RESPONSIBLE_USERS_COUNT_REGION,
  ENDPOINT_RESPONSIBLE_USERS_COUNT_REPUBLIC,
  ENDPOINT_RESPONSIBLE_USER_CONTOUR_ACTIONS,
  ENDPOINT_RESPONSIBLE_USER_PROJECTS,
  ENDPOINT_RESPONSIBLE_USER_PROJECT_CONTOURS,
  ENDPOINT_RESPONSIBLE_USER_PROJECT_MONTHS,
  ENDPOINT_STEWARDSHIP_EMPLOYEE_ACTIONS,
  ENDPOINT_STEWARDSHIP_REGION_ACTIONS,
  ENDPOINT_STEWARDSHIP_REPUBLIC_ACTIONS,
} from '@/common/constants';
import { HttpApi } from '@/common/http';
import { IPageable } from '@/common/models';
import { create } from 'zustand';
import {
  IActionInspectData,
  IContourAction,
  IDistrictActionsData,
  IEmployeeActions,
  IProjectContour,
  IRepublicActionsData,
  IResponsibleEmployee,
  IResponsibleUserProject,
  IResponsibleUsersCount,
  IResponsibleUsersCountRegion,
  IResponsibleUsersCountRepublic,
  TStewardship,
} from '../models/IStewardship';
import { cleanObject } from '@/utils/clean-object';

const httpApi = new HttpApi();

export const useStewardshipStore = create<TStewardship>((set) => ({
  responsibleUsersCount: null,
  responsibleUsersCountRepublic: [],
  responsibleUsersCountRegion: [],
  republicActionsData: [],
  republicActionsCachedAt: null,
  republicActionsLoading: false,
  districtActionsData: [],
  districtActionsCachedAt: null,
  districtActionsLoading: false,
  responsibleUsersCountLoading: false,
  responsibleUsersCountRepublicLoading: false,
  responsibleUsersCountRegionLoading: false,
  responsibleEmployees: [],
  responsibleEmployeesLoading: false,
  employeeActions: null,
  employeeActionsLoading: false,
  responsibleUserProjects: null,
  responsibleUserProjectsLoading: false,
  projectMonths: [],
  projectMonthsLoading: false,
  projectContours: [],
  projectContoursLoading: false,
  contourActions: {},
  contourActionsLoading: {},
  actionInspectData: null,
  actionInspectLoading: false,
  fetchResponsibleUsersCount: async (params: {
    plant_id: string;
    region_id?: string;
    district_id?: string;
    farmer_type?: string;
  }) => {
    set({ responsibleUsersCountLoading: true });
    try {
      const res = await httpApi.get<IResponsibleUsersCount>(
        ENDPOINT_RESPONSIBLE_USERS_COUNT,
        cleanObject(params),
      );
      set({ responsibleUsersCount: res });
    } catch (error) {
      console.error('Error fetching responsible users count:', error);
      set({ responsibleUsersCount: null });
    } finally {
      setTimeout(() => {
        set({ responsibleUsersCountLoading: false });
      }, 300);
    }
  },
  fetchResponsibleUsersCountRepublic: async (params: {
    plant_id: string;
    region_id?: string;
    farmer_type?: string;
  }) => {
    set({ responsibleUsersCountRepublicLoading: true });
    try {
      const res = await httpApi.get<IResponsibleUsersCountRepublic[]>(
        ENDPOINT_RESPONSIBLE_USERS_COUNT_REPUBLIC,
        cleanObject(params),
      );
      set({ responsibleUsersCountRepublic: res || [] });
    } catch (error) {
      console.error('Error fetching responsible users count republic:', error);
      set({ responsibleUsersCountRepublic: [] });
    } finally {
      setTimeout(() => {
        set({ responsibleUsersCountRepublicLoading: false });
      }, 300);
    }
  },
  fetchResponsibleUsersCountRegion: async (params: {
    plant_id: string;
    region_id: string;
    farmer_type?: string;
  }) => {
    set({ responsibleUsersCountRegionLoading: true });
    try {
      const res = await httpApi.get<IResponsibleUsersCountRegion[]>(
        ENDPOINT_RESPONSIBLE_USERS_COUNT_REGION,
        cleanObject(params),
      );
      set({ responsibleUsersCountRegion: res || [] });
    } catch (error) {
      console.error('Error fetching responsible users count region:', error);
      set({ responsibleUsersCountRegion: [] });
    } finally {
      setTimeout(() => {
        set({ responsibleUsersCountRegionLoading: false });
      }, 300);
    }
  },
  fetchRepublicActionsData: async (params: { plant_id: string; farmer_type?: string }) => {
    set({ republicActionsLoading: true });
    try {
      const res = await httpApi.get<{ cachedAt: string, data: IRepublicActionsData[] }>(
        ENDPOINT_STEWARDSHIP_REPUBLIC_ACTIONS,
        cleanObject(params),
      );
      set({ republicActionsData: res?.data || [], republicActionsCachedAt: res?.cachedAt || null });
    } catch (error) {
      console.error('Error fetching region-list actions data:', error);
      set({ republicActionsData: [], republicActionsCachedAt: null });
    } finally {
      setTimeout(() => {
        set({ republicActionsLoading: false });
      }, 300);
    }
  },
  fetchDistrictActionsData: async (params: {
    plant_id: string;
    region_id: string;
    organization_id: string;
    farmer_type?: string;
  }) => {
    set({ districtActionsLoading: true });
    try {
      const res = await httpApi.get<{ cachedAt: string, data: IDistrictActionsData[] }>(
        `${ENDPOINT_STEWARDSHIP_REGION_ACTIONS}/${params.region_id}`,
        cleanObject(params),
      );
      set({ districtActionsData: res?.data || [], districtActionsCachedAt: res?.cachedAt || null });
    } catch (error) {
      console.error('Error fetching district-list actions data:', error);
      set({ districtActionsData: [], districtActionsCachedAt: null });
    } finally {
      setTimeout(() => {
        set({ districtActionsLoading: false });
      }, 300);
    }
  },
  fetchResponsibleEmployees: async (params: {
    district_id: string;
    plant_id: string;
    organization_id: string;
    farmer_type?: string;
  }) => {
    set({ responsibleEmployeesLoading: true });
    try {
      const res = await httpApi.get<IResponsibleEmployee[]>(
        `${ENDPOINT_RESPONSIBLE_USERS_BY_DISTRICT}/${params.district_id}`,
        cleanObject(params),
      );
      set({ responsibleEmployees: res || [] });
    } catch (error) {
      console.error('Error fetching responsible employees:', error);
      set({ responsibleEmployees: [] });
    } finally {
      setTimeout(() => {
        set({ responsibleEmployeesLoading: false });
      }, 300);
    }
  },
  fetchEmployeeActions: async (params: {
    employee_id: string;
    plant_id: string;
    district_id: string;
    farmer_type?: string;
  }) => {
    set({ employeeActionsLoading: true });
    try {
      const res = await httpApi.get<IEmployeeActions>(
        `${ENDPOINT_STEWARDSHIP_EMPLOYEE_ACTIONS}/${params.employee_id}`,
        cleanObject({ plant_id: params.plant_id, district_id: params.district_id, farmer_type: params.farmer_type }),
      );
      set({ employeeActions: res || null });
    } catch (error) {
      console.error('Error fetching employee actions:', error);
      set({ employeeActions: null });
    } finally {
      setTimeout(() => {
        set({ employeeActionsLoading: false });
      }, 300);
    }
  },
  fetchResponsibleUserProjects: async (params: {
    user_id: string;
    plant_id: string;
    district_id: string;
    page?: string;
    limit?: string;
    tax_number?: string;
    farmer_type?: string;
  }) => {
    set({ responsibleUserProjectsLoading: true });
    try {
      const queryParams: Record<string, string> = {
        plant_id: params.plant_id,
        district_id: params.district_id,
      };
      if (params.page) queryParams.page = params.page;
      if (params.limit) queryParams.limit = params.limit;
      if (params.tax_number) queryParams.tax_number = params.tax_number;
      if (params.farmer_type) queryParams.farmer_type = params.farmer_type;

      const res = await httpApi.get<IPageable<IResponsibleUserProject>>(
        `${ENDPOINT_RESPONSIBLE_USER_PROJECTS}/${params.user_id}`,
        queryParams,
      );
      set({ responsibleUserProjects: res || null });
    } catch (error) {
      console.error('Error fetching responsible user projects:', error);
      set({ responsibleUserProjects: null });
    } finally {
      setTimeout(() => {
        set({ responsibleUserProjectsLoading: false });
      }, 300);
    }
  },
  fetchProjectMonths: async (params: {
    project_id: string;
    user_id: string;
  }) => {
    set({ projectMonthsLoading: true });
    try {
      const res = await httpApi.get<string[]>(
        `${ENDPOINT_RESPONSIBLE_USER_PROJECT_MONTHS}/${params.project_id}`,
        { user_id: params.user_id },
      );
      set({ projectMonths: res || [] });
    } catch (error) {
      console.error('Error fetching project months:', error);
      set({ projectMonths: [] });
    } finally {
      setTimeout(() => {
        set({ projectMonthsLoading: false });
      }, 300);
    }
  },
  fetchProjectContours: async (params: {
    project_id: string;
    month: string;
  }) => {
    set({ projectContoursLoading: true });
    try {
      const res = await httpApi.get<IProjectContour[]>(
        `${ENDPOINT_RESPONSIBLE_USER_PROJECT_CONTOURS}/${params.project_id}`,
        { month: params.month },
      );
      set({ projectContours: res || [] });
    } catch (error) {
      console.error('Error fetching project contours:', error);
      set({ projectContours: [] });
    } finally {
      setTimeout(() => {
        set({ projectContoursLoading: false });
      }, 300);
    }
  },
  fetchContourActions: async (params: {
    contour_id: string;
    project_id: string;
    month: string;
    user_id: string;
  }) => {
    set((state) => ({
      contourActionsLoading: {
        ...state.contourActionsLoading,
        [params.contour_id]: true,
      },
    }));
    try {
      const res = await httpApi.get<IContourAction[]>(
        `${ENDPOINT_RESPONSIBLE_USER_CONTOUR_ACTIONS}/${params.project_id}/${params.month}/${params.contour_id}`,
        { user_id: params.user_id },
      );
      set((state) => ({
        contourActions: {
          ...state.contourActions,
          [params.contour_id]: res || [],
        },
      }));
    } catch (error) {
      console.error('Error fetching contour actions:', error);
      set((state) => ({
        contourActions: {
          ...state.contourActions,
          [params.contour_id]: [],
        },
      }));
    } finally {
      setTimeout(() => {
        set((state) => ({
          contourActionsLoading: {
            ...state.contourActionsLoading,
            [params.contour_id]: false,
          },
        }));
      }, 300);
    }
  },
  fetchActionInspect: async (params: {
    farmer_action_by_project_id: string;
    farmer_action_by_project_contour_id: string | null;
  }) => {
    set({ actionInspectLoading: true });
    try {
      const queryParams: Record<string, string> = {};
      if (params.farmer_action_by_project_contour_id) {
        queryParams.farmer_action_by_project_contour_id =
          params.farmer_action_by_project_contour_id;
      }

      const res = await httpApi.get<IActionInspectData>(
        `${ENDPOINT_FARMER_ACTION_INSPECT}/${params.farmer_action_by_project_id}`,
        queryParams,
      );
      set({ actionInspectData: res || null });
    } catch (error) {
      console.error('Error fetching action inspect data:', error);
      set({ actionInspectData: null });
    } finally {
      setTimeout(() => {
        set({ actionInspectLoading: false });
      }, 300);
    }
  },
  clearActionInspectData: () => {
    set({ actionInspectData: null, actionInspectLoading: false });
  },
}));
